import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
	Badge,
	Box,
	Button,
	FormControl,
	HStack,
	Icon,
	SimpleGrid,
	Td,
	Text,
	Textarea,
	Tr,
	useColorModeValue,
	useDisclosure,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiFileText, FiInfo, FiMessageSquare, FiSend, FiUser } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
	usePropertyModalTheme,
} from "../PropertyModal/shared";
import { addReplyToMessageAsync } from "../../redux/user/userSlice";
import { fullDateTimeFormat } from "../../utils/types";

export default function MessagesTableRow(props) {
	const { row, author, property } = props;
	const { t } = useTranslation();
	const textColor = useColorModeValue("gray.700", "white");
	const pendingStatusBackground = "blue";
	const resolvedStatusBackground = "green";
	const descriptionBg = useColorModeValue("gray.50", "gray.700");
	const { borderColor, iconColor } = usePropertyModalTheme();

	const { isOpen, onOpen, onClose } = useDisclosure();
	const dispatch = useDispatch();
	const initialReply = { message: "" };
	const [reply, setReply] = useState(initialReply);

	const handleChange = (e) => {
		setReply({ message: e.target.value });
	};

	const saveTicket = (e) => {
		const payload = { author, reply, messageId: row.id, propertyId: property.id };
		dispatch(addReplyToMessageAsync(payload)).finally(() => {
			onClose(e);
		});
	};

	const latestReply = row?.replies.toSorted((a, b) => moment(b.timestamp).diff(moment(a.timestamp)))[0]?.timestamp;

	const typeBadge = (
		<Badge
			colorScheme={row.type === "tenant" ? resolvedStatusBackground : pendingStatusBackground}
			fontSize="xs"
			px={2}
			py={0.5}
			borderRadius="full"
		>
			{t(`table.${row.type}`)}
		</Badge>
	);

	const renderMessageModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={onClose}
			size="4xl"
			icon={FiMessageSquare}
			title={t("property.messageDetailsTitle")}
			subtitle={t("property.viewMessageSubtitle")}
			headerExtra={typeBadge}
			footer={
				<ModalFooterActions
					onCancel={onClose}
					onConfirm={saveTicket}
					isDisabled={row.status === "done"}
					confirmIcon={FiSend}
				/>
			}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiInfo} title={t("property.messageInformation")}>
						<VStack spacing={3} align="stretch">
							<Box>
								<HStack spacing={2} mb={1}>
									<Icon as={FiFileText} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.title")}
									</Text>
								</HStack>
								<Text fontSize="sm" fontWeight="bold">
									{row.title}
								</Text>
							</Box>
							<Box>
								<HStack spacing={2} mb={1}>
									<Icon as={FiCalendar} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.date")}
									</Text>
								</HStack>
								<Text fontSize="sm">{moment(row.addedDate).format("DD MMMM YYYY - HH:mm")}</Text>
							</Box>
							<Box>
								<HStack spacing={2} mb={1}>
									<Icon as={FiUser} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.author")}
									</Text>
								</HStack>
								<Text fontSize="sm">{row.author.name}</Text>
							</Box>
							<Box>
								<HStack spacing={2} mb={1}>
									<Icon as={FiMessageSquare} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.description")}
									</Text>
								</HStack>
								<Text fontSize="sm" bg={descriptionBg} p={3} borderRadius="md">
									{row.description}
								</Text>
							</Box>
						</VStack>
					</PropertyModalSection>
				</VStack>

				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection
						icon={FiMessageSquare}
						title={t("property.messageReplies")}
						headerExtra={
							<Badge colorScheme="blue" fontSize="xs" px={2} py={0.5} borderRadius="full">
								{row.replies.length}
							</Badge>
						}
					>
						{row.replies.length > 0 ? (
							<VStack spacing={3} align="stretch">
								{row.replies.map((m, index) => (
									<Box key={index} p={3} border="1px" borderColor={borderColor} borderRadius="md" bg={descriptionBg}>
										<VStack align="stretch" spacing={2}>
											<HStack justify="space-between">
												<HStack spacing={2}>
													<Icon as={FiUser} color={iconColor} boxSize={4} />
													<Text fontSize="sm" fontWeight="bold">
														{m.author.name}
													</Text>
												</HStack>
												<Text fontSize="xs" color="gray.500">
													{moment(m.timestamp).format("DD MMM YYYY - HH:mm")}
												</Text>
											</HStack>
											<Text fontSize="sm" pl={6}>
												{m.message}
											</Text>
										</VStack>
									</Box>
								))}
							</VStack>
						) : (
							<Box textAlign="center" py={4}>
								<Icon as={FiMessageSquare} color="gray.300" boxSize={6} mb={2} />
								<Text fontSize="sm" color="gray.500">
									{t("property.noRepliesYet")}
								</Text>
							</Box>
						)}
					</PropertyModalSection>

					{row.status !== "done" && (
						<PropertyModalSection icon={FiSend} title={t("property.addReply")}>
							<FormControl>
								<ModalFieldLabel icon={FiMessageSquare}>{t("property.yourResponse")}</ModalFieldLabel>
								<Textarea
									size="sm"
									name="message"
									placeholder={t("property.typeYourMessage")}
									value={reply.message}
									onChange={handleChange}
									borderRadius="md"
									rows={4}
								/>
							</FormControl>
						</PropertyModalSection>
					)}
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	return (
		<Tr>
			<Td>
				<Text fontSize="md" minWidth="100%">
					{latestReply ? moment(latestReply).format(fullDateTimeFormat) : t("property.noRepliesYet")}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" minWidth="100%">
					{property.owner.name}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{row.title}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{row.author.name}
				</Text>
			</Td>
			<Td>
				<Badge colorScheme={row.type === "tenant" ? resolvedStatusBackground : pendingStatusBackground} fontSize="16px" p="3px 10px" borderRadius="8px">
					{t(`table.${row.type}`)}
				</Badge>
			</Td>
			<Td>
				<Text fontSize="md" color={textColor} pb=".5rem">
					{moment(row.addedDate).format("DD MMMM YYYY - HH:mm")}
				</Text>
			</Td>
			<Td>
				<Button onClick={onOpen} colorScheme="blue">
					<Text fontSize="md">{t("table.view")}</Text>
				</Button>
				{renderMessageModal()}
			</Td>
		</Tr>
	);
}
